// Per-user opt-in toggle for the 7am "your follow-ups today" email, plus a
// personal schedule dialog (work days + leave dates). Mounted in the sidebar
// user footer so every portal user can reach it.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sun, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
  ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];

const ScheduleDialog = ({ open, onClose, prefs, onSaved }) => {
  const [workDays, setWorkDays] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWorkDays(prefs.work_days?.length ? prefs.work_days : DAYS.map(([k]) => k));
    setHolidays(prefs.personal_holidays || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const flipDay = (k) => setWorkDays((prev) => (
    prev.includes(k) ? prev.filter((d) => d !== k) : [...prev, k]
  ));

  const addDate = () => {
    if (!newDate || holidays.includes(newDate)) { setNewDate(''); return; }
    setHolidays((prev) => [...prev, newDate].sort());
    setNewDate('');
  };

  const saveAll = async () => {
    if (!workDays.length) { toast.error('Pick at least one working day'); return; }
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/me/email-prefs`, {
        work_days: workDays, personal_holidays: holidays,
      });
      onSaved(r.data);
      toast.success('Schedule saved — morning emails follow your working days');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save schedule');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="work-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarDays className="w-4 h-4 text-teal-600" /> My work days &amp; leave
          </DialogTitle>
          <DialogDescription className="text-xs">
            Your morning email only arrives on days you work. Add leave dates to pause it while you're away.
          </DialogDescription>
        </DialogHeader>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide opacity-60 mb-1.5">Days I work</p>
          <div className="flex gap-1.5 flex-wrap">
            {DAYS.map(([k, label]) => (
              <button key={k} onClick={() => flipDay(k)}
                className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${
                  workDays.includes(k)
                    ? 'bg-teal-600 border-teal-600 text-white'
                    : 'border-slate-200 text-slate-400 hover:border-slate-300'}`}
                data-testid={`work-day-${k}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide opacity-60 mb-1.5">My leave / days off</p>
          <div className="flex items-center gap-2 mb-2">
            <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-transparent"
              data-testid="personal-holiday-input" />
            <button onClick={addDate} disabled={!newDate}
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-40"
              data-testid="personal-holiday-add-btn">Add</button>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="personal-holiday-list">
            {holidays.map((d) => (
              <span key={d} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-slate-100 rounded-full px-2 py-0.5">
                {d}
                <button onClick={() => setHolidays((prev) => prev.filter((x) => x !== d))}
                  className="opacity-50 hover:opacity-100 font-bold" data-testid={`personal-holiday-remove-${d}`}>×</button>
              </span>
            ))}
            {!holidays.length && <span className="text-[11px] opacity-50 italic">No leave dates added.</span>}
          </div>
        </div>
        <button onClick={saveAll} disabled={saving}
          className="w-full text-sm font-bold py-2 rounded-full bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50"
          data-testid="work-schedule-save-btn">
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const MorningEmailToggle = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [schedOpen, setSchedOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/me/email-prefs`)
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({ morning_email_enabled: false, work_days: [], personal_holidays: [] }));
  }, []);

  const enabled = !!prefs?.morning_email_enabled;

  const flip = async () => {
    if (saving || prefs === null) return;
    setSaving(true);
    const next = !enabled;
    try {
      const r = await axios.patch(`${API}/me/email-prefs`, { morning_email_enabled: next });
      setPrefs(r.data);
      toast.success(next
        ? 'Morning email on — your follow-ups arrive at 7:00am on your working days'
        : 'Morning email off');
    } catch {
      toast.error('Could not save preference');
    }
    setSaving(false);
  };

  return (
    <>
      <button
        onClick={flip}
        disabled={prefs === null || saving}
        className="w-full flex items-center px-4 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50"
        data-testid="morning-email-toggle"
        title="Daily 7:00am email listing the follow-ups due today, with a one-click sign-in link"
      >
        <Sun className="w-4 h-4 mr-2 text-amber-500" />
        Morning email
        <span className={`ml-auto text-[10px] uppercase tracking-wide font-bold ${enabled ? 'text-teal-600' : 'text-slate-400'}`}>
          {prefs === null ? '…' : enabled ? 'on' : 'off'}
        </span>
      </button>
      {enabled && (
        <button
          onClick={() => setSchedOpen(true)}
          className="w-full flex items-center px-4 py-1.5 rounded-md text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 mb-1"
          data-testid="work-schedule-open-btn"
          title="Choose the days you work and add leave dates — no morning email on days off"
        >
          <CalendarDays className="w-3.5 h-3.5 mr-2 text-teal-600" />
          Work days &amp; leave
          {(prefs?.personal_holidays || []).length > 0 && (
            <span className="ml-auto text-[10px] font-bold text-teal-600">{prefs.personal_holidays.length} leave</span>
          )}
        </button>
      )}
      {prefs !== null && (
        <ScheduleDialog open={schedOpen} onClose={() => setSchedOpen(false)}
          prefs={prefs} onSaved={setPrefs} />
      )}
    </>
  );
};
